<template>

  <BasicLayout>
    <ItemPage :options="options" :searchData="searchData" :quickSearch="quickSearch" :items="items"></ItemPage>
    <v-dialog v-model="dialog" max-width="1000px">
      <v-card>
        <v-card-title class="headline">Details</v-card-title>
        <v-card-text>
          <v-data-table v-if="detailsItem" :headers="headers" :items="detailsItem.videos" hide-actions="hide-actions" no-data-text="No video">
            <template slot="items" slot-scope="props">
              <td>{{ props.item.id }}</td>
              <td>{{ props.item.authorPreferredUsername }}</td>
              <td>{{ props.item.type }}</td>
              <td>{{ props.item.club1Name }}</td>
              <td>{{ props.item.club2Name }}</td>
              <td>{{ props.item.scorer }}</td>
              <td>{{ props.item.hitCounter }}</td>
              <td>{{ props.item.voteIncAnonymousCounter }}</td>
            </template>
          </v-data-table>
          <div class="text-center" v-else>
            <v-progress-circular indeterminate="indeterminate" color="primary"></v-progress-circular>
          </div>
          <div class="error-message">{{ error }}</div>
        </v-card-text>
        <v-layout justify-end="justify-end" row="row" align-center="align-center">
          <v-flex xs2="xs2" offset-xs8="offset-xs8" text-xs-right="text-xs-right">{{ selectedUser }}</v-flex>
          <v-flex xs3="xs3" text-xs-right="text-xs-right">
            <v-btn @click="selectRandomUser" :disabled="voteList.length === 0">Pick Random Voter</v-btn>
          </v-flex>
        </v-layout>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </BasicLayout>

</template>

<script>

import BasicLayout from '@/layouts/BasicLayout'
import ItemPage from '@/components/common/ItemPage'
import SportSelector from '@/components/SportSelector'
import ItemID from '@/components/links/ItemID'
import DatePicker from '@/components/common/DatePicker'
import DateSearch from '@/components/common/DateSearch'
import TopActionTypeSelector from '@/components/TopActionTypeSelector'
import TopActionTypeSearch from '@/components/TopActionTypeSearch'
import TopActionDate from '@/components/common/TopActionDate'
import TopActionDatePicker from '@/components/common/TopActionDatePicker'
import SportLink from '@/components/links/Sport'
import TopActionVideoPicker from '@/components/TopActionVideoPicker'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'TopActions',
  components: {
    BasicLayout,
    ItemPage
  },
  data () {
    return {
      itemIDs: [],
      dialog: null,
      detailsItem: null,
      error: '',
      selectedUser: '',
      options: {
        title: 'Top Actions',
        createMaxWidth: '1000px',
        applyCreate: this.create,
        editable: false,
        applyDelete: this.del,
        itemActions: [
          {
            icon: 'mdi-poll-box',
            title: 'Details',
            onclick: this.details
          }
        ],
        columns: [
          {
            text: 'Type',
            value: 'type',
            editComponent: TopActionTypeSelector,
            searchComponent: TopActionTypeSearch,
            display: false,
            editable: false,
            sortable: false
          },
          {
            text: 'Date',
            value: 'date',
            displayComponent: TopActionDate,
            editComponent: TopActionDatePicker,
            searchComponent: DateSearch,
            placeholder: 'YYYY/MM/DD hh:mm:ss',
            sortable: false
          },
          {
            text: 'Sport',
            value: 'sportID',
            displayComponent: SportLink,
            editComponent: SportSelector,
            editPosition: 1,
            sortable: false,
            editable: false
          },
          {
            text: 'ID',
            value: 'id',
            displayComponent: ItemID,
            editable: false,
            creatable: false,
            searchable: false,
            sortable: false
          },
          {
            text: 'Deadline',
            value: 'deadline',
            displayRender: d => new Date(d).toLocaleString(`${this.$localeRegion}-${this.$country}`),
            editComponent: DatePicker,
            searchComponent: DateSearch,
            placeholder: 'YYYY/MM/DD hh:mm:ss',
            sortable: false
          },
          {
            text: 'Partner',
            value: 'partner',
            displayRender: p => p || '-'
          },
          {
            text: 'Videos',
            value: 'videos',
            editComponent: TopActionVideoPicker,
            displayable: false,
            searchable: false
          },
          {
            text: 'Videos',
            value: 'videos',
            displayRender: v => v ? v.length || 0 : 0,
            creatable: false,
            editable: false,
            searchable: false
          },
          {
            text: 'Votes',
            value: 'videos',
            displayRender: v => v.reduce((s, e) => s + e.voteIncAnonymousCounter, 0),
            creatable: false,
            editable: false,
            searchable: false
          }
        ]
      },
      headers: [
        {
          text: 'ID',
          value: 'id'
        },
        {
          text: 'Author',
          value: 'authorID'
        },
        {
          text: 'Type',
          value: 'type'
        },
        {
          text: 'Club 1',
          value: 'club1Name'
        },
        {
          text: 'Club 2',
          value: 'club2Name'
        },
        {
          text: 'Scorer',
          value: 'scorer'
        },
        {
          text: 'Views',
          value: 'hitCounter'
        },
        {
          text: 'Votes',
          value: 'voteIncAnonymousCounter'
        }
      ]
    }
  },
  computed: {
    ...mapState(['topActions']),
    ...mapGetters(['topActionByIDs', 'topActionValues']),

    items () {
      return this.topActionByIDs(this.itemIDs)
    },

    voteList () {
      const list = []

      if (this.detailsItem && this.detailsItem.videos) {
        for (const video of this.detailsItem.videos) {
          if (video.votes) {
            list.push(...video.votes)
          }
        }
      }
      return list
    }
  },
  methods: {
    searchData (params) {
      return new Promise((resolve, reject) => {
        if (params.date) {
          params.startDate = params.date.from
          params.endDate = params.date.to
          delete params.date
        }

        if (params.deadline) {
          params.startDeadline = params.deadline.from
          params.endDeadline = params.deadline.to
          delete params.deadline
        }

        if (params.club1ID) {
          params.clubID = params.club1ID
          delete params.club1ID
        }

        if (params.club2ID) {
          params.otherClubID = params.club2ID
          delete params.club2ID
        }

        params.withTotalItems = true

        this.$store.dispatch('searchTopActions', params)
          .then(resp => {
            this.itemIDs = resp.items.map(i => i.id)
            resolve(resp.totalItems)
          })
          .catch(err => {
            console.error('Failed to load top actions: ', err)
            this.itemIDs = []
            reject(err)
          })
      })
    },
    quickSearch (input) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('getTopAction', input)
          .then(resp => {
            this.itemIDs = [resp.id]
            resolve(1)
          })
          .catch(err => {
            console.error('Failed to load top action:', err)
            this.itemIDs = []
            reject(err)
          })
      })
    },
    create (item) {
      if (new Date(item.deadline) < new Date()) {
        return Promise.reject(new Error('The deadline is already passed'))
      }
      if (!item.videos || item.videos.length === 0) {
        return Promise.reject(new Error('There is no video'))
      }
      if (item.type === 'week') {
        item.date = this.monday(item.date)
      }
      if (item.partner === '') {
        delete item.partner
      }
      return this.$store.dispatch('createTopAction', item)
        .then(top => {
          this.itemIDs = [top.id, ...this.itemIDs.splice(-1)]
          return top.id
        })
    },
    del (item) {
      return this.$store.dispatch('deleteTopAction', item.id)
    },
    details (item) {
      this.dialog = true
      this.$store.dispatch('getTopAction', item.id)
        .then(top => {
          setTimeout(() => {
            this.detailsItem = top
          }, 1000)
        })
        .catch(err => {
          this.error = 'Failed to load the topaction'
          console.error(err)
        })
    },
    monday (originalDate) {
      const d = new Date(originalDate)
      let weekday = d.getUTCDay()
      let diff = d.getUTCDate() - weekday + (weekday === 0 ? -6 : 1) // adjust when day is sunday
      return new Date(d.setUTCDate(diff))
    },
    selectRandomUser () {
      this.selectedUser = this.voteList[Math.floor(Math.random() * this.voteList.length)]
    }
  },
  watch: {
    dialog () {
      if (this.dialog === false) {
        this.dialog = false
        this.detailsItem = null
        this.error = ''
        this.selectedUser = ''
      }
    }
  }
}

</script>

<style lang="scss" scoped>

.error-message {
  color: red;
  text-align: center;
}

</style>



// WEBPACK FOOTER //
// src/pages/TopActions.vue